import { api } from "./api.js";
import type { ImapTargets } from "./mailActions.js";

// ── Types ───────────────────────────────────────────────────────────────────
export type ThreadStatus = "open" | "archived" | "spam";
export type Direction = "inbound" | "outbound";

export interface ClientRef { id: string; name: string; kind: string; email?: string | null }

export interface MailAttachment {
  name: string;
  contentType: string;
  size: number;
  /**
   * Set when the file's bytes are actually held. Absent on a message from
   * before the mailbox kept them, and on one whose write to storage failed -
   * both mean the paperclip is a record, not a download. The value itself is
   * never the storage key: the server sends a boolean-ish marker and keeps the
   * key, because the bucket behind it is public-read.
   */
  stored?: boolean;
}

/** A file uploaded from the composer but not yet attached to a sent message. */
export interface PendingAttachment {
  /** What a send refers to. The storage key stays on the server. */
  id: string;
  name: string;
  contentType: string;
  size: number;
}

/** What the mailbox will carry, served rather than duplicated in the client. */
export interface AttachmentLimits {
  maxBytes: number;
  maxTotalBytes: number;
  maxFiles: number;
  /** Lowercase, with the dot. Refused at the picker so a file no gateway would
   *  deliver is not uploaded in full first. */
  blockedExtensions: readonly string[];
}

export interface MailMessage {
  id: string;
  threadId: string;
  direction: Direction;
  messageId: string | null;
  inReplyTo: string | null;
  references: string | null;
  fromAddress: string;
  fromName: string | null;
  toAddresses: string[];
  ccAddresses: string[];
  /** The Reply-To a received message named, empty when it named none. An
   *  answer goes here rather than to `fromAddress` (see `replyRecipients`).
   *  Optional because an API older than the field does not send it. */
  replyToAddresses?: string[];
  /** Blind copies. Only ever on our own outbound messages: an inbound message
   *  never says who was blind-copied on it. Optional because an API older than
   *  the field does not send it, and a thread must still open against one. */
  bccAddresses?: string[];
  subject: string;
  bodyText: string | null;
  bodyHtml: string | null;
  snippet: string | null;
  attachments: MailAttachment[] | null;
  status: string; // received | sent | failed | dev
  error: string | null;
  sentByUserId: string | null;
  createdAt: string;
  sentAt: string | null;
}

export interface MailThread {
  id: string;
  subject: string;
  status: ThreadStatus;
  unread: boolean;
  starred: boolean;
  /** Null: the shared mailbox. An id: one of the reader's own mailboxes. */
  mailboxId?: string | null;
  clientId: string | null;
  ticketId: string | null;
  conversationId: string | null;
  /** The outreach campaign that started this thread, when one did. A loose ref
   *  to MktCampaign.id: the marketing module owns the row, the inbox only
   *  filters and links on it. Same pattern as `ticketId`. */
  campaignId: string | null;
  /** Operator label ids; the names and colours live in `crm.labels.mail`
   *  (`lib/crmLabels.ts`). An id with no label behind it draws nothing. */
  tags: string[];
  matchedRegCode: string | null;
  matchedCompanyName: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  client?: ClientRef | null;
  messages?: MailMessage[];
  _count?: { messages: number };
}

export interface MailStats {
  unread: number;
  open: number;
  /** Threads any campaign started. */
  campaignThreads: number;
  /** Whether inbound polling is on. A campaign whose reply address is not
   *  polled has an inbox that stays empty forever, and the campaign readiness
   *  strip is the only place that can say so. */
  inboundEnabled: boolean;
  /** The mailbox the poller reads, so marketing can compare it to a reply-to. */
  inboundAddress: string;
  /** The address a letter leaves from unless the writer picks another. Not
   *  `inboundAddress`: the mailbox reads one account and may send as several.
   *  Optional because an API older than the sender list does not send it. */
  sendingAddress?: string;
  /** The caller's own drafts, and the ones of them with a send time. Optional
   *  because an API older than drafts does not send them. */
  drafts?: number;
  scheduled?: number;
}

/** The thread list filters. Named rather than an open bag: `campaignId` is easy
 *  to misspell into silence when every key is a string. */
export interface ThreadQuery {
  q?: string;
  status?: ThreadStatus;
  unread?: boolean;
  matched?: boolean;
  starred?: boolean;
  /** Threads carrying at least one message with a file on it. */
  hasAttachment?: boolean;
  /** Oldest first turns the folder into a backlog queue. Absent is newest. */
  sort?: "newest" | "oldest";
  clientId?: string;
  campaignId?: string;
  /** Absent means every thread; false means only threads no campaign started. */
  hasCampaign?: boolean;
  /** One operator label id. Absent means every thread, whatever it is labelled. */
  tag?: string;
  /** "shared" (absent means the same), "all", or an own mailbox id. */
  mailbox?: string;
  page?: number;
  pageSize?: number;
}

/** One saved sign-off. `name` is empty on the one folded in from the old
 *  single field - the interface labels that one itself. */
export interface MailSignature {
  id: string;
  name: string;
  text: string;
  /** Add the company card (the automatic letters' signature) under the text. */
  card?: boolean;
}

/** The company card as the composer previews it. Null from the API when it is empty. */
export interface MailCardPreview {
  closing: string;
  name: string;
  details: string;
  accent: string;
  logoUrl: string;
}

/**
 * One sending address as the settings page edits it. The password never comes
 * back: `smtpPassSet` says one is stored, and `smtpPass` is only ever what the
 * owner has just typed (empty keeps the stored one).
 */
export interface MailSenderRow {
  id: string;
  address: string;
  /** Empty: the default sender's name is used. */
  name: string;
  /** Offered in the composer. The default is always on. */
  active: boolean;
  /** Its own SMTP login. Empty: the mailbox's login. */
  smtpUser: string;
  smtpPassSet: boolean;
  smtpPass?: string;
}

/** One address the composer may send as, with its name already filled in. */
export interface ComposerSender {
  address: string;
  name: string;
  /** Which mailbox it belongs to: "shared" or an own mailbox id. Optional
   *  because an API older than own mailboxes does not send it. */
  mailboxId?: string;
}

/** One mailbox in the rail: the shared one, or one of the reader's own. */
export interface MailboxView {
  id: string;
  kind: "shared" | "own";
  address: string;
  name: string;
  unread: number;
  open: number;
  drafts: number;
  scheduled: number;
  /** Its folders on the mail server can be listed. */
  imap: boolean;
  /** This person may open messages there. */
  imapOpen: boolean;
  /** Why its last read failed, in the server's words. */
  error: string | null;
  polledAt: string | null;
}

/** The servers the add form starts from: the shared mailbox's, the same provider as a rule. */
export interface MailServerDefaults {
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
}

export interface OwnMailboxForm extends MailServerDefaults {
  address: string;
  name: string;
  /** Empty: the address itself. */
  login: string;
  /** Required on add. Empty on a change keeps the stored one. */
  pass: string;
}

export interface MailSettings {
  fromAddress: string;
  fromName: string;
  outboundMode: "smtp" | "resend";
  smtp: { host: string; port: number; secure: boolean; user: string; passSet: boolean };
  resendApiKeySet: boolean;
  imap: { host: string; port: number; secure: boolean; user: string; passSet: boolean; lastUid: number };
  inbound: { enabled: boolean; pollSeconds: number };
  /** The original single signature. Still stored, still editable; the list
   *  below folds it in, so read `signatures` and not this. */
  signature: string;
  signatures: MailSignature[];
  defaultSignatureId: string;
  /** Every sending address, the default one included. The default row is
   *  also `fromAddress` / `fromName` above. */
  senders: MailSenderRow[];
  defaultSenderId: string;
  autoMatch: boolean;
  webhookUrl: string;
  outboundConfigured: boolean;
  imapConfigured: boolean;
}

export interface SyncResult {
  ok: boolean;
  ingested: number;
  highestUid: number;
  /** The run only recorded the high-water mark - the mailbox's history is skipped. */
  baseline?: boolean;
  reason?: string;
}

/** A folder that exists on the mail server itself, not one of the rail's saved queries. */
export interface ImapFolder {
  path: string;
  name: string;
  parentPath: string;
  depth: number;
  specialUse: string | null;
  selectable: boolean;
  subscribed: boolean;
  /** Null when the mailbox had too many folders to count them all. */
  messages: number | null;
  unseen: number | null;
}

export interface ImapFolderList {
  ok: boolean;
  folders: ImapFolder[];
  delimiter: string;
  reason?: string;
}

/** One message as the folder listing draws it: headers only, no body. */
export interface ImapMessageRow {
  uid: number;
  seq: number;
  subject: string;
  fromName: string | null;
  fromAddress: string;
  to: string[];
  date: string | null;
  size: number;
  seen: boolean;
  flagged: boolean;
  answered: boolean;
  draft: boolean;
  hasAttachments: boolean;
}

export interface ImapMessagePage {
  ok: boolean;
  rows: ImapMessageRow[];
  /** Messages in the folder, which is not `rows.length` - the list is the newest slice. */
  total: number;
  limit: number;
  /** The folder's UIDVALIDITY, handed back on a read so a folder that was
   *  deleted and recreated cannot answer with a different message. */
  uidValidity: string;
  /** The folder's own special-use flag ("\\Trash"). Optional because an API
   *  older than the right-click menu does not send it. */
  specialUse?: string | null;
  /** Where one-click Archive, Spam and Delete send a message. See `mailActions.ts`. */
  targets?: ImapTargets;
  reason?: string;
}

export interface ImapMessageBody {
  uid: number;
  subject: string;
  fromName: string | null;
  fromAddress: string;
  to: string[];
  cc: string[];
  date: string | null;
  size: number;
  seen: boolean;
  html: string | null;
  text: string | null;
  attachments: MailAttachment[];
  /** The message was too big to fetch; there is no body to show. */
  tooLarge: boolean;
}

export interface ImapMessageResult {
  ok: boolean;
  message?: ImapMessageBody;
  reason?: string;
}

interface Page<T> { rows: T[]; total: number; page: number; pageSize: number; pageCount: number }

function qs(params?: Record<string, string | number | boolean | undefined>): string {
  if (!params) return "";
  const p = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`);
  return p.length ? `?${p.join("&")}` : "";
}

// ── Threads and messages ─────────────────────────────────────────────────────
export const getThreads = (params?: ThreadQuery) =>
  api.get<Page<MailThread>>(`/admin/crm/mail/threads${qs(params as Record<string, string | number | boolean | undefined>)}`);
export const getMailStats = () => api.get<MailStats>("/admin/crm/mail/stats");
export const getThread = (id: string) =>
  api.get<MailThread & { messages: MailMessage[] }>(`/admin/crm/mail/threads/${id}`);

// `signatureId` has three states and the wire keeps all three: an id names one,
// `null` is the writer sending without one, and leaving it out asks for the
// mailbox default. Anything that omits it therefore signs as it always did.
export const sendMail = (body: {
  to: string[]; cc?: string[]; bcc?: string[]; subject: string; body: string; bodyHtml?: string;
  clientId?: string | null; attachmentIds?: string[]; signatureId?: string | null;
  /** One of the mailbox's sending addresses. Absent: the default sender. */
  fromAddress?: string;
  /** The composer's saved draft of this letter, deleted by the server once it is sent. */
  draftId?: string;
}) => api.post<{ threadId: string; message: MailMessage; transport: string }>("/admin/crm/mail/send", body);
export const replyThread = (
  id: string,
  body: {
    body: string; bodyHtml?: string; cc?: string[]; bcc?: string[]; to?: string[];
    attachmentIds?: string[]; signatureId?: string | null;
    /** One of the mailbox's sending addresses. Absent: the default sender. */
    fromAddress?: string;
    /** The composer's saved draft of this reply, deleted by the server once it is sent. */
    draftId?: string;
  },
) => api.post<{ message: MailMessage; transport: string }>(`/admin/crm/mail/threads/${id}/reply`, body);
export const patchThread = (id: string, body: Record<string, unknown>) =>
  api.patch<MailThread>(`/admin/crm/mail/threads/${id}`, body);
export const convertToTicket = (id: string) =>
  api.post<{ ticketId: string; number: string }>(`/admin/crm/mail/threads/${id}/convert-ticket`, {});
export const suggestMailReply = (id: string) =>
  api.post<{ text: string }>(`/admin/crm/mail/threads/${id}/suggest-reply`, {});
export const syncMail = (mailbox?: string) =>
  api.post<SyncResult>(`/admin/crm/mail/sync${qs({ mailbox })}`, {});

// ── Mailboxes and Favorites ─────────────────────────────────────────────────
export const getMailboxes = () =>
  api.get<{ mailboxes: MailboxView[]; defaults: MailServerDefaults; max: number }>("/admin/crm/mail/mailboxes");
export const getOwnMailbox = (id: string) =>
  api.get<Omit<OwnMailboxForm, "pass"> & { id: string; passSet: boolean }>(`/admin/crm/mail/mailboxes/${id}`);
export const addOwnMailbox = (body: OwnMailboxForm) => api.post<{ id: string }>("/admin/crm/mail/mailboxes", body);
export const updateOwnMailbox = (id: string, body: OwnMailboxForm) =>
  api.put<{ id: string }>(`/admin/crm/mail/mailboxes/${id}`, body);
export const removeOwnMailbox = (id: string) =>
  api.del<{ ok: boolean; threads: number }>(`/admin/crm/mail/mailboxes/${id}`);
export const getMailFavorites = () => api.get<{ favorites: string[] }>("/admin/crm/mail/favorites");
export const saveMailFavorites = (favorites: string[]) =>
  api.put<{ favorites: string[] }>("/admin/crm/mail/favorites", { favorites });
/** The order the person dragged their mailboxes into, as mailbox ids. */
export const saveMailboxOrder = (order: string[]) =>
  api.put<{ order: string[] }>("/admin/crm/mail/mailboxes/order", { order });

// ── Attachments ──────────────────────────────────────────────────────────────
// Staging is separate from sending on purpose: an operator picks five files and
// then writes for ten minutes, and a Send that had to move 20MB first would
// look like a frozen window. Each file goes up as it is picked and the send
// names only the ids.
export const getAttachmentLimits = () =>
  api.get<AttachmentLimits>("/admin/crm/mail/attachments/limits");

export const uploadAttachment = (file: File, signal?: AbortSignal) => {
  const form = new FormData();
  form.append("file", file, file.name);
  return api.upload<PendingAttachment>("/admin/crm/mail/attachments", form, signal);
};

/**
 * Stage a file the media library already holds.
 *
 * The MEDIA id, not a storage key: the picker just handed one over, and the key
 * stays server-side because the bucket behind it is public-read. The server
 * copies the bytes, so deleting the picture later cannot empty an attachment on
 * a letter that has already gone out.
 */
export const attachFromMedia = (mediaId: string) =>
  api.post<PendingAttachment>("/admin/crm/mail/attachments/from-media", { mediaId });

export const deleteAttachment = (id: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/mail/attachments/${id}`);

/**
 * Where a stored file is downloaded from - a plain URL, for a plain link.
 *
 * By INDEX into the message's own list rather than by key: the key is the
 * server's, and an endpoint that took one would be a read over the whole bucket
 * wearing a mail route's name. A link rather than a fetch because the browser's
 * own download machinery handles the save dialog, the progress and the resume,
 * and the session cookie travels with it - the API is same-origin here.
 */
export const attachmentUrl = (messageId: string, index: number) =>
  `/api/admin/crm/mail/messages/${encodeURIComponent(messageId)}/attachments/${index}`;

/** The same, for a message that only exists on the mail server. */
export const imapAttachmentUrl = (path: string, uid: number, index: number, uidValidity?: string, mailbox?: string) =>
  `/api/admin/crm/mail/imap/attachment${qs({ path, uid, index, uidValidity, mailbox })}`;

// ── The mail server's own folders ────────────────────────────────────────────
// Every call below takes the mailbox it acts on: absent is the shared one.
export const getImapFolders = (mailbox?: string) =>
  api.get<ImapFolderList>(`/admin/crm/mail/imap/folders${qs({ mailbox })}`);
export const createImapFolder = (body: { name: string; parentPath?: string; mailbox?: string }) =>
  api.post<{ path: string; created: boolean }>("/admin/crm/mail/imap/folders", body);
// The folder path travels in the body, never in the URL: the character joining
// its segments is the server's own choice, and a "/" in there is a path
// separator to every proxy in between.
export const renameImapFolder = (body: { path: string; name: string; mailbox?: string }) =>
  api.post<{ from: string; path: string }>("/admin/crm/mail/imap/folders/rename", body);
export const deleteImapFolder = (path: string, mailbox?: string) =>
  api.post<{ path: string; messages: number | null }>("/admin/crm/mail/imap/folders/delete", { path, mailbox });
export const deleteThread = (id: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/mail/threads/${id}`);

// Reading a folder is a GET with the path in the query - a query value is
// opaque to a proxy, which is exactly what a path SEGMENT is not, and a read
// the browser can repeat and abort belongs on GET.
export const getImapMessages = (path: string, limit?: number, signal?: AbortSignal, mailbox?: string) =>
  api.get<ImapMessagePage>(`/admin/crm/mail/imap/messages${qs({ path, limit, mailbox })}`, signal);
export const getImapMessage = (
  path: string,
  uid: number,
  uidValidity?: string,
  signal?: AbortSignal,
  mailbox?: string,
) => api.get<ImapMessageResult>(`/admin/crm/mail/imap/message${qs({ path, uid, uidValidity, mailbox })}`, signal);

// ── Settings (Integrations) ──────────────────────────────────────────────────
export const getMailSettings = () => api.get<MailSettings>("/admin/crm/mail/settings");

/** Only what a composer needs to draw the From field: no logins. */
export const getMailSenders = () =>
  api.get<{ senders: ComposerSender[]; defaultAddress: string }>("/admin/crm/mail/senders");
export const saveMailSettings = (body: Record<string, unknown>) =>
  api.put<{ ok: boolean }>("/admin/crm/mail/settings", body);

// ── Sending channels ─────────────────────────────────────────────────────────
// Three separate sending identities: the platform account, the operator's own
// account, and a third one for bulk. `resolution` is what the server would
// ACTUALLY do, which is not the same question as whether the fields are filled.
export type MailChannelId = "system" | "marketing";
export type ChannelMode = "platform" | "mailbox" | "resend" | "smtp";

export interface MailChannelView {
  enabled: boolean;
  mode: ChannelMode;
  fromAddress: string;
  fromName: string;
  replyTo: string;
  resendApiKeySet: boolean;
  /**
   * Where the provider posts back what became of a letter. Complete, with the
   * token in it - it is an address to paste, not a secret to protect, and the
   * server builds it from the PUBLIC origin so it is never the compose address.
   */
  webhookUrl: string;
  /** The provider's signing secret is stored. The value itself never comes back. */
  webhookSecretSet: boolean;
  smtp: { host: string; port: number; secure: boolean; user: string; passSet: boolean };
  resolution: "platform" | "own" | "unavailable";
  reason: string | null;
  effectiveFrom: string;
  /** The channel would send over the same account as the platform: not isolated. */
  sharesPlatformAccount: boolean;
}

export interface MailChannelsResponse {
  allowedModes: Record<MailChannelId, ChannelMode[]>;
  channels: Record<MailChannelId, MailChannelView>;
}

export interface ChannelTestResult { ok: boolean; status: string; error: string | null }

// ── Test mode (the outbound guard) ───────────────────────────────────────────
// Client-facing mail is readdressed to a test inbox while the system is being
// exercised against live data. Auth and billing mail is exempt on the server, so
// nothing here can lock a real customer out of their account.
export interface MailGuard {
  enabled: boolean;
  /** Where held messages go instead. Empty while enabled means nothing is sent. */
  redirectTo: string;
  /** Full addresses (`a@b.ee`) or whole domains (`b.ee`) that go out untouched. */
  allowlist: string[];
}

/** What the server WOULD do with these recipients right now. Never computed in
 *  the browser: a dialog that promises one thing while the server does another
 *  teaches the operator to trust a sentence nothing checks. */
export interface MailGuardCheck {
  enabled: boolean;
  outcome: "pass" | "redirect" | "blocked";
  redirectTo: string | null;
  intended: string[];
  reason: string | null;
}

export const getMailGuard = () => api.get<MailGuard>("/admin/crm/mail/guard");
export const saveMailGuard = (body: Partial<MailGuard>) => api.put<MailGuard>("/admin/crm/mail/guard", body);
export const checkMailGuard = (to: string[], cc?: string[], bcc?: string[]) =>
  api.post<MailGuardCheck>("/admin/crm/mail/guard/check", { to, cc, bcc });

export const getMailChannels = () => api.get<MailChannelsResponse>("/admin/crm/mail/channels");
export const saveMailChannel = (id: MailChannelId, body: Record<string, unknown>) =>
  api.put<MailChannelsResponse>(`/admin/crm/mail/channels/${id}`, body);
export const testMailChannel = (id: MailChannelId, to: string) =>
  api.post<ChannelTestResult>(`/admin/crm/mail/channels/${id}/test`, { to });
